// ============================================
// SCRIPT DE MIGRACIÓN: LocalStorage → PostgreSQL
// ============================================
// Este script migra todos los datos de localStorage
// a la base de datos PostgreSQL

import { authAPI } from '../api/auth';
import { employeesAPI } from '../api/employees';
import { departmentsAPI, positionsAPI, shiftsAPI, schedulesAPI, 
        vacacionesAPI, permisosAPI, lunchMenusAPI } from '../api/services';

class MigrationService {
  constructor() {
    this.results = {
      success: [],
      errors: [],
      skipped: []
    };
  }

  // ============================================
  // MIGRAR DEPARTAMENTOS
  // ============================================
  async migrateDepartments() {
    console.log('📦 Migrando Departamentos...');
    
    const departments = JSON.parse(localStorage.getItem('7family_departments') || '[]');
    
    for (const dept of departments) {
      try {
        await departmentsAPI.create({
          nombre: dept.nombre,
          descripcion: dept.descripcion || '',
          codigo: dept.codigo || dept.id
        });
        this.results.success.push(`Departamento: ${dept.nombre}`);
        console.log(`✅ ${dept.nombre}`);
      } catch (error) {
        this.results.errors.push(`Departamento ${dept.nombre}: ${error.message}`);
        console.error(`❌ ${dept.nombre}:`, error.message);
      }
    }
    
    return departments.length;
  }

  // ============================================
  // MIGRAR POSICIONES
  // ============================================
  async migratePositions() {
    console.log('📦 Migrando Posiciones...');
    
    const positions = JSON.parse(localStorage.getItem('7family_positions') || '[]');
    const departments = JSON.parse(localStorage.getItem('7family_departments') || '[]');
    
    // Obtener departamentos de la BD para hacer match
    const dbDepartments = await departmentsAPI.getAll();
    
    for (const pos of positions) {
      try {
        // Buscar departamento correspondiente
        const localDept = departments.find(d => d.id === pos.departamentoId);
        const dbDept = dbDepartments.find(d => d.nombre === localDept?.nombre);
        
        await positionsAPI.create({
          nombre: pos.nombre,
          department_id: dbDept?.id || null,
          descripcion: pos.descripcion || '',
          nivel_jerarquico: pos.nivel || 1,
          salario_minimo: pos.salarioMinimo || 0,
          salario_maximo: pos.salarioMaximo || 0
        });
        
        this.results.success.push(`Posición: ${pos.nombre}`);
        console.log(`✅ ${pos.nombre}`);
      } catch (error) {
        this.results.errors.push(`Posición ${pos.nombre}: ${error.message}`);
        console.error(`❌ ${pos.nombre}:`, error.message);
      }
    }
    
    return positions.length;
  }

  // ============================================
  // MIGRAR EMPLEADOS
  // ============================================
  async migrateEmployees() {
    console.log('👥 Migrando Empleados...');
    
    const employees = JSON.parse(localStorage.getItem('7family_employees') || '[]');
    const departments = await departmentsAPI.getAll();
    const positions = await positionsAPI.getAll();
    
    for (const emp of employees) {
      try {
        // Buscar IDs correspondientes
        const dept = departments.find(d => d.nombre === emp.departamento);
        const pos = positions.find(p => p.nombre === emp.cargo);
        
        const employeeData = {
          employee_id: emp.employeeId,
          nombre: emp.nombre,
          apellido: emp.apellido,
          cedula: emp.cedula,
          fecha_nacimiento: emp.fechaNacimiento || null,
          edad: emp.edad || null,
          sexo: emp.sexo || null,
          nacionalidad: emp.nacionalidad || 'Dominicana',
          pais: emp.pais || 'República Dominicana',
          tipo_sangre: emp.tipoSangre || null,
          profesion: emp.profesion || null,
          telefono: emp.telefono || null,
          celular: emp.celular || null,
          email: emp.email || null,
          direccion: emp.direccion || null,
          department_id: dept?.id || null,
          position_id: pos?.id || null,
          cargo: emp.cargo || null,
          fecha_ingreso: emp.fechaIngreso || null,
          salario: emp.salario ? parseFloat(emp.salario.toString().replace(/,/g, '')) : null,
          tipo_contrato: emp.tipoContrato || 'indefinido',
          tipo_nomina: emp.tipoNomina || 'Mensual',
          estado_empleado: emp.estadoEmpleado || 'Activo',
          turno: emp.turno || null,
          tipo_empleado: emp.tipoEmpleado || 'Tiempo Completo',
          nss: emp.nss || null,
          descuento_afp: emp.descuentoAFP !== false,
          descuento_sfs: emp.descuentoSFS !== false,
          descuento_isr: emp.descuentoISR !== false,
          nivel_educativo: emp.nivelEducativo || null,
          contacto_emergencia: emp.contactoEmergencia || null,
          nombre_banco: emp.nombreBanco || null,
          cuenta_banco: emp.cuentaBanco || null,
          foto: emp.foto || null,
          estado: emp.estado || 'activo'
        };
        
        await employeesAPI.create(employeeData);
        this.results.success.push(`Empleado: ${emp.nombre} ${emp.apellido}`);
        console.log(`✅ ${emp.nombre} ${emp.apellido}`);
        
      } catch (error) {
        this.results.errors.push(`Empleado ${emp.nombre}: ${error.message}`);
        console.error(`❌ ${emp.nombre}:`, error.message);
      }
    }
    
    return employees.length;
  }

  // ============================================
  // MIGRAR TURNOS
  // ============================================
  async migrateShifts() {
    console.log('⏰ Migrando Turnos...');
    
    const shifts = JSON.parse(localStorage.getItem('7family_shifts') || '[]');
    
    for (const shift of shifts) {
      try {
        await shiftsAPI.create({
          nombre: shift.nombre,
          descripcion: shift.descripcion || '',
          hora_entrada: shift.horaEntrada,
          hora_salida: shift.horaSalida,
          minutos_tolerancia: shift.minutosTolerancia || 15,
          color: shift.color || '#3B82F6'
        });
        
        this.results.success.push(`Turno: ${shift.nombre}`);
        console.log(`✅ ${shift.nombre}`);
      } catch (error) {
        this.results.errors.push(`Turno ${shift.nombre}: ${error.message}`);
        console.error(`❌ ${shift.nombre}:`, error.message);
      }
    }
    
    return shifts.length;
  }

  // ============================================
  // MIGRAR HORARIOS
  // ============================================
  async migrateSchedules() {
    console.log('📅 Migrando Horarios...');
    
    const schedules = JSON.parse(localStorage.getItem('7family_schedules') || '[]');
    const dbEmployees = await employeesAPI.getAll();
    const dbShifts = await shiftsAPI.getAll();
    
    for (const schedule of schedules) {
      try {
        const employee = dbEmployees.employees?.find(e => e.employee_id === schedule.employeeId);
        const shift = dbShifts.find(s => s.nombre === schedule.turnoNombre);
        
        if (!employee || !shift) {
          this.results.skipped.push(`Horario (faltan refs): ${schedule.id}`);
          continue;
        }
        
        await schedulesAPI.create({
          employee_id: employee.id,
          shift_id: shift.id,
          dia_semana: schedule.diaSemana,
          fecha_inicio: schedule.fechaInicio || null,
          fecha_fin: schedule.fechaFin || null
        });
        
        this.results.success.push(`Horario para empleado ${employee.nombre}`);
        console.log(`✅ Horario creado`);
      } catch (error) {
        this.results.errors.push(`Horario: ${error.message}`);
        console.error(`❌ Horario:`, error.message);
      }
    }
    
    return schedules.length;
  }

  // ============================================
  // MIGRAR VACACIONES
  // ============================================
  async migrateVacaciones() {
    console.log('🏖️ Migrando Vacaciones...');
    
    const vacaciones = JSON.parse(localStorage.getItem('7family_vacaciones') || '[]');
    const dbEmployees = await employeesAPI.getAll();
    
    for (const vac of vacaciones) {
      try {
        const employee = dbEmployees.employees?.find(e => e.employee_id === vac.employeeId);
        
        if (!employee) {
          this.results.skipped.push(`Vacaciones (empleado no existe): ${vac.id}`);
          continue;
        }
        
        await vacacionesAPI.create({
          employee_id: employee.id,
          fecha_inicio: vac.fechaInicio,
          fecha_fin: vac.fechaFin,
          dias_solicitados: vac.diasSolicitados,
          dias_disponibles: vac.diasDisponibles || vac.diasSolicitados,
          tipo: vac.tipo || 'vacaciones',
          motivo: vac.motivo || '',
          estado: vac.estado || 'pendiente'
        });
        
        this.results.success.push(`Vacaciones para ${employee.nombre}`);
        console.log(`✅ Vacaciones migradas`);
      } catch (error) {
        this.results.errors.push(`Vacaciones: ${error.message}`);
        console.error(`❌ Vacaciones:`, error.message);
      }
    }
    
    return vacaciones.length;
  }

  // ============================================
  // MIGRAR PERMISOS
  // ============================================
  async migratePermisos() {
    console.log('📝 Migrando Permisos...');
    
    const permisos = JSON.parse(localStorage.getItem('7family_permisos') || '[]');
    const dbEmployees = await employeesAPI.getAll();
    
    for (const permiso of permisos) {
      try {
        const employee = dbEmployees.employees?.find(e => e.employee_id === permiso.employeeId);
        
        if (!employee) {
          this.results.skipped.push(`Permiso (empleado no existe): ${permiso.id}`);
          continue;
        }
        
        await permisosAPI.create({
          employee_id: employee.id,
          tipo: permiso.tipo || 'personal',
          fecha_inicio: permiso.fechaInicio,
          fecha_fin: permiso.fechaFin,
          horas: permiso.horas || null,
          motivo: permiso.motivo,
          estado: permiso.estado || 'pendiente',
          es_remunerado: permiso.esRemunerado || false
        });
        
        this.results.success.push(`Permiso para ${employee.nombre}`);
        console.log(`✅ Permiso migrado`);
      } catch (error) {
        this.results.errors.push(`Permiso: ${error.message}`);
        console.error(`❌ Permiso:`, error.message);
      }
    }
    
    return permisos.length;
  }

  // ============================================
  // MIGRAR MENÚS DE ALMUERZO
  // ============================================
  async migrateLunchMenus() {
    console.log('🍽️ Migrando Menús de Almuerzo...');
    
    const menus = JSON.parse(localStorage.getItem('7family_lunch_menus') || '[]');
    
    for (const menu of menus) {
      try {
        await lunchMenusAPI.create({
          nombre: menu.nombre,
          dia: menu.dia,
          semana: menu.semana,
          opcion: menu.opcion,
          items: menu.items,
          descripcion: menu.descripcion || '',
          precio: menu.precio || 0,
          disponible: menu.disponible !== false,
          activo: menu.activo !== false
        });
        
        this.results.success.push(`Menú: ${menu.nombre}`);
        console.log(`✅ ${menu.nombre}`);
      } catch (error) {
        this.results.errors.push(`Menú ${menu.nombre}: ${error.message}`);
        console.error(`❌ ${menu.nombre}:`, error.message);
      }
    }
    
    return menus.length;
  }

  // ============================================
  // EJECUTAR MIGRACIÓN COMPLETA
  // ============================================
  async runFullMigration() {
    console.log('');
    console.log('🚀 ================================');
    console.log('🚀 INICIANDO MIGRACIÓN COMPLETA');
    console.log('🚀 LocalStorage → PostgreSQL');
    console.log('🚀 ================================');
    console.log('');

    const startTime = Date.now();

    try {
      // Orden importante: primero las dependencias
      const deptCount = await this.migrateDepartments();
      console.log(`\n📊 Departamentos procesados: ${deptCount}\n`);

      const posCount = await this.migratePositions();
      console.log(`\n📊 Posiciones procesadas: ${posCount}\n`);

      const empCount = await this.migrateEmployees();
      console.log(`\n📊 Empleados procesados: ${empCount}\n`);

      const shiftCount = await this.migrateShifts();
      console.log(`\n📊 Turnos procesados: ${shiftCount}\n`);

      const schedCount = await this.migrateSchedules();
      console.log(`\n📊 Horarios procesados: ${schedCount}\n`);

      const vacCount = await this.migrateVacaciones();
      console.log(`\n📊 Vacaciones procesadas: ${vacCount}\n`);

      const permCount = await this.migratePermisos();
      console.log(`\n📊 Permisos procesados: ${permCount}\n`);

      const menuCount = await this.migrateLunchMenus();
      console.log(`\n📊 Menús procesados: ${menuCount}\n`);

      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);

      // Resumen
      console.log('');
      console.log('✅ ================================');
      console.log('✅ MIGRACIÓN COMPLETADA');
      console.log('✅ ================================');
      console.log(`⏱️  Tiempo: ${duration}s`);
      console.log(`✅ Exitosos: ${this.results.success.length}`);
      console.log(`❌ Errores: ${this.results.errors.length}`);
      console.log(`⏭️  Omitidos: ${this.results.skipped.length}`);
      console.log('');

      if (this.results.errors.length > 0) {
        console.log('❌ Errores encontrados:');
        this.results.errors.forEach(err => console.log(`   - ${err}`));
      }

      return {
        success: true,
        stats: {
          duration,
          successful: this.results.success.length,
          errors: this.results.errors.length,
          skipped: this.results.skipped.length
        },
        results: this.results
      };

    } catch (error) {
      console.error('❌ Error fatal en migración:', error);
      return {
        success: false,
        error: error.message,
        results: this.results
      };
    }
  }
}

export default MigrationService;
